import { useEffect } from 'react';

import type { QueryFunction } from '@tanstack/react-query';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { useAppState } from '../../AppStateProvider';
import type { Company } from '../../domain/company';
import { Page } from '../../domain/page';
import { useFilterCollectedDomains } from '../../hooks/useFilterCollectedDomains';
import { appStore } from '../../services/appStore';
import { useDispatchTrackEventWithLocation } from '../../services/tracking/hooks/useDispatchTrackEventWithLocation';
import { invalidateDataCreditsQuery } from '../quota/useDataCreditsQuery';

import { companyApi } from './api';
import companyQueryKey from './companyQueryKey';
import { updateCompanyQueryData } from './updateQueryData';
import { invalidateCompanyQuotaQuery, useCompanyQuotaQuery } from './useCompanyQuotaQuery';

const getFirstCompanyByName = (companies: Company[]) =>
  companies.reduce((min, company) => (company.name && (!min.name || company.name < min.name) ? company : min));

export const useCompaniesQuery = () => {
  const queryClient = useQueryClient();
  const { page } = useAppState();

  const dispatchTrackEventWithLocation = useDispatchTrackEventWithLocation();

  const companyQuota = useCompanyQuotaQuery().data;

  const filteredDomains = useFilterCollectedDomains();

  const shouldSortData = page !== Page.GoogleSheets;

  const fetchCompaniesInfo: QueryFunction<Company[]> = async ({ signal }) => {
    const { data, status } = await companyApi.getCompaniesInfo(filteredDomains, signal);
    if (status === 206) {
      dispatchTrackEventWithLocation('usage limits', 'limit reached', 'overviews');
      appStore.setCompanyQuotaReached(true);
    }

    const companies = data ?? [];

    if (companies.length > 0) {
      if (companyQuota?.isUnlimited === false) {
        invalidateCompanyQuotaQuery();
        invalidateDataCreditsQuery();
      }
      companies.forEach((company) => updateCompanyQueryData({ company, queryClient }));
    }

    return companies;
  };

  const query = useQuery({
    queryKey: companyQueryKey.companies(filteredDomains),
    queryFn: fetchCompaniesInfo,
    enabled: filteredDomains.length > 0,
    gcTime: Infinity,
  });

  useEffect(() => {
    if (!query.data || query.data.length === 0) {
      return;
    }
    const firstCompany = shouldSortData ? getFirstCompanyByName(query.data) : query.data[0];
    appStore.setCompanyDomain(firstCompany.domain);
  }, [query.data, shouldSortData]);

  return query;
};
